import { useEffect, useRef, useState, type Ref } from "react";
import {
  Check,
  ChevronDown,
  Loader2,
  Pencil,
  RefreshCw,
  Sparkles,
  Wand2,
  X,
} from "lucide-react";
import {
  aiErrorMessage,
  aiService,
  EVIDENCE_ACTIONS,
  WRITING_ACTIONS,
  AI_UNAVAILABLE,
  type AIContext,
  type AIOperation,
  type AIStatus,
} from "../services/aiService";
import { useAIStatusQuery } from "../hooks/useAIStatus";

// Outside an appraisal there is no evidence file: the one drafting action is
// the context's own, and the rewrites are the three that suit short records.
const CONTEXT_REWRITES: AIOperation[] = ["improve", "make_professional", "make_concise"];

interface Props {
  value: string;
  onChange: (value: string, provenance: { ai_generated: boolean; ai_edited: boolean }) => void;
  /** Fetched here when not given. */
  status?: AIStatus;
  appraisalId?: number;
  /** Help outside an appraisal: which records the server reads to draft. */
  context?: AIContext;
  /** The drafting button's label in a context, e.g. "Draft description". */
  draftLabel?: string;
  /** Tells the reader what the draft works from, under the buttons. */
  draftHint?: string;
  inputRef?: Ref<HTMLTextAreaElement>;
  autoFocus?: boolean;
  name?: string;
  label?: string;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
  /** Narrow the offered operations, e.g. to the generation ones on a
   * strengths field. */
  operations?: AIOperation[];
}

/**
 * A textarea with optional AI drafting beside it.
 *
 * Three things this component is careful about:
 *
 * 1. If AI is unavailable or the user cannot use it, it renders as an ordinary
 *    textarea with no controls and no mention of AI. The field must work
 *    exactly as well without an assistant.
 * 2. A suggestion is never written into the field. It appears beneath it,
 *    plainly marked, and the person accepts, edits or rejects it. Nothing
 *    reaches the appraisal until they do.
 * 3. Accepting carries provenance up to the caller, so the saved answer records
 *    whether it was AI-written or AI-drafted-then-edited. That flag is
 *    permanent and shown on the record.
 *
 * The controls follow from the same idea: the assistant is offered, never
 * pushed. Drafting from evidence is the one visible button because it is the
 * only operation that does anything on a blank field; the rewrites sit behind
 * a single menu and stay disabled until there is text to rework, instead of
 * lining up six buttons that cannot yet do anything.
 */
export default function AIAssistField({
  value,
  onChange,
  status: givenStatus,
  appraisalId,
  context,
  draftLabel = "Draft with AI",
  draftHint,
  inputRef,
  autoFocus,
  name,
  label,
  placeholder,
  rows = 5,
  disabled = false,
  operations,
}: Props) {
  const [suggestion, setSuggestion] = useState<string | null>(null);
  const [interactionId, setInteractionId] = useState<number | null>(null);
  const [lastOperation, setLastOperation] = useState<AIOperation | null>(null);
  const [editedSuggestion, setEditedSuggestion] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [busy, setBusy] = useState<AIOperation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const statusQuery = useAIStatusQuery(!givenStatus);
  const status = givenStatus ?? statusQuery.data ?? AI_UNAVAILABLE;
  // Someone allowed to use the assistant always sees where it is, even while
  // it is loading or not answering; only a person it is not for sees nothing.
  const checking = !givenStatus && !statusQuery.data && statusQuery.fetchStatus === "fetching";
  const unreachable = !givenStatus && statusQuery.isError;
  const allowed = status.enabled && status.can_assist && !disabled;
  const aiUsable = allowed && status.available;
  const offered = operations
    ? WRITING_ACTIONS.filter((a) => operations.includes(a.operation))
    : WRITING_ACTIONS;
  // Drafting from evidence works on an empty field; the rewrites need
  // something to rewrite, so the two are kept apart rather than mixed.
  const drafting = context
    ? [{ operation: "draft" as AIOperation, label: draftLabel }]
    : offered.filter((a) => EVIDENCE_ACTIONS.includes(a.operation));
  const rewrites = context
    ? WRITING_ACTIONS.filter((a) => CONTEXT_REWRITES.includes(a.operation))
    : offered.filter((a) => !EVIDENCE_ACTIONS.includes(a.operation));
  const hasText = value.trim().length > 0;
  const words = value.trim() ? value.trim().split(/\s+/).length : 0;

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const run = async (operation: AIOperation) => {
    setBusy(operation);
    setMenuOpen(false);
    setError(null);
    try {
      const result = await aiService.assist(operation, {
        text: value,
        appraisal: appraisalId,
        context,
      });
      setSuggestion(result.suggestion);
      setEditedSuggestion(result.suggestion);
      setInteractionId(result.interaction_id);
      setLastOperation(operation);
      setIsEditing(false);
    } catch (err) {
      setError(aiErrorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const dismiss = () => {
    setSuggestion(null);
    setInteractionId(null);
    setIsEditing(false);
  };

  const accept = () => {
    const text = isEditing ? editedSuggestion : (suggestion ?? "");
    const wasEdited = isEditing && editedSuggestion !== suggestion;
    onChange(text, { ai_generated: !wasEdited, ai_edited: wasEdited });
    if (interactionId) {
      void aiService.recordOutcome(interactionId, wasEdited ? "edited" : "accepted");
    }
    dismiss();
  };

  const reject = () => {
    if (interactionId) void aiService.recordOutcome(interactionId, "rejected");
    dismiss();
  };

  const operationLabel =
    [...drafting, ...WRITING_ACTIONS].find((a) => a.operation === lastOperation)?.label ?? "Suggestion";

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-sm font-medium text-secondary-700">{label}</label>
      )}

      <div className="relative">
        <textarea
          ref={inputRef}
          name={name}
          autoFocus={autoFocus}
          className={`input w-full resize-y py-2.5 leading-relaxed ${context ? "h-auto min-h-20" : "min-h-28"}`}
          rows={rows}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          onChange={(e) =>
            onChange(e.target.value, { ai_generated: false, ai_edited: false })
          }
        />
        {words > 0 && (
          <span className="pointer-events-none absolute bottom-2 right-3 text-xs tabular-nums text-secondary-400">
            {words} {words === 1 ? "word" : "words"}
          </span>
        )}
      </div>

      {(checking || unreachable || (allowed && !status.available)) && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {checking ? (
            <span className="inline-flex items-center gap-1.5 text-secondary-500">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-500" aria-hidden />
              Checking the writing assistant…
            </span>
          ) : (
            <>
              <span className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 font-medium text-amber-800">
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
                {unreachable
                  ? "The writing assistant could not be reached."
                  : "The writing assistant is not answering right now — it may still be starting up."}
              </span>
              <button
                type="button"
                className="inline-flex items-center gap-1 font-semibold text-violet-700 hover:underline disabled:opacity-50"
                disabled={statusQuery.isFetching}
                onClick={() => void statusQuery.refetch()}
              >
                <RefreshCw className={`h-3.5 w-3.5 ${statusQuery.isFetching ? "animate-spin" : ""}`} aria-hidden />
                Try again
              </button>
            </>
          )}
        </div>
      )}

      {aiUsable && (
        <div className="flex flex-wrap items-center gap-2">
          {drafting.map((action) => (
            <button
              key={action.operation}
              type="button"
              className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-xs font-semibold text-violet-700 transition-colors hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1 disabled:opacity-50"
              disabled={busy !== null}
              onClick={() => void run(action.operation)}
            >
              {busy === action.operation ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Sparkles className="h-3.5 w-3.5" aria-hidden />
              )}
              {action.label}
            </button>
          ))}

          {rewrites.length > 0 && (
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-secondary-300 bg-white px-3 py-1.5 text-xs font-semibold text-secondary-700 transition-colors hover:bg-secondary-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:opacity-50"
                disabled={busy !== null || !hasText}
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                title={
                  hasText
                    ? undefined
                    : "Write something first, then the assistant can rework it."
                }
                onClick={() => setMenuOpen((open) => !open)}
              >
                {busy !== null && !drafting.some((d) => d.operation === busy) ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                ) : (
                  <Wand2 className="h-3.5 w-3.5" aria-hidden />
                )}
                Rework this
                <ChevronDown className="h-3.5 w-3.5 text-secondary-400" aria-hidden />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute left-0 top-full z-20 mt-1 w-52 overflow-hidden rounded-lg border border-secondary-200 bg-white py-1 shadow-lg"
                >
                  {rewrites.map((action) => (
                    <button
                      key={action.operation}
                      type="button"
                      role="menuitem"
                      className="block w-full px-3 py-2 text-left text-sm text-secondary-700 hover:bg-violet-50 hover:text-violet-800"
                      onClick={() => void run(action.operation)}
                    >
                      {action.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <span className="text-xs text-secondary-400">
            {draftHint ?? "You decide what goes in. Nothing is written for you."}
          </span>
        </div>
      )}

      {error && (
        <p
          className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
          role="status"
        >
          {error}
        </p>
      )}

      {busy !== null && suggestion === null && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4" role="status">
          <div className="mb-3 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-violet-700">
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            Drafting
            {context && (
              <span className="font-normal normal-case tracking-normal text-violet-500">
                — the school's own model can take up to a minute
              </span>
            )}
          </div>
          <div className="space-y-2" aria-hidden>
            <div className="h-3 w-full animate-pulse rounded bg-violet-200/70" />
            <div className="h-3 w-11/12 animate-pulse rounded bg-violet-200/70" />
            <div className="h-3 w-3/5 animate-pulse rounded bg-violet-200/70" />
          </div>
        </div>
      )}

      {suggestion !== null && (
        <div className="overflow-hidden rounded-xl border border-violet-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-2 border-b border-violet-100 bg-gradient-to-r from-violet-50 to-indigo-50 px-4 py-2.5">
            <span className="inline-flex flex-wrap items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-violet-600">
                <Sparkles className="h-3.5 w-3.5 text-white" aria-hidden />
              </span>
              <span className="text-sm font-semibold text-violet-900">
                {operationLabel}
              </span>
              <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-violet-700 ring-1 ring-violet-200">
                Not saved yet
              </span>
            </span>
            <button
              type="button"
              className="shrink-0 rounded p-1 text-violet-500 transition-colors hover:bg-white hover:text-violet-800"
              onClick={reject}
              aria-label="Dismiss suggestion"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          </div>

          <div className="px-4 py-3">
            {isEditing ? (
              <textarea
                className="input w-full resize-y py-2.5 leading-relaxed"
                rows={rows}
                value={editedSuggestion}
                onChange={(e) => setEditedSuggestion(e.target.value)}
              />
            ) : (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-secondary-800">
                {suggestion}
              </p>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-secondary-100 bg-secondary-50/70 px-4 py-3">
            <button
              type="button"
              className="inline-flex min-h-9 items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-violet-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1"
              onClick={accept}
            >
              <Check className="h-3.5 w-3.5" aria-hidden />
              {isEditing ? "Use edited text" : "Use this"}
            </button>
            {!isEditing && (
              <button
                type="button"
                className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-secondary-300 bg-white px-3 py-1.5 text-xs font-semibold text-secondary-700 transition-colors hover:bg-secondary-50"
                onClick={() => setIsEditing(true)}
              >
                <Pencil className="h-3.5 w-3.5" aria-hidden />
                Edit first
              </button>
            )}
            <button
              type="button"
              className="inline-flex min-h-9 items-center gap-1.5 rounded-lg border border-secondary-300 bg-white px-3 py-1.5 text-xs font-semibold text-secondary-700 transition-colors hover:bg-secondary-50 disabled:opacity-50"
              disabled={busy !== null || !lastOperation}
              onClick={() => lastOperation && void run(lastOperation)}
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${busy !== null ? "animate-spin" : ""}`}
                aria-hidden
              />
              Try again
            </button>
            <button
              type="button"
              className="min-h-9 px-2 text-xs font-medium text-secondary-500 transition-colors hover:text-secondary-800"
              onClick={reject}
            >
              Discard
            </button>

            <p className="ml-auto max-w-xs text-right text-[11px] leading-snug text-secondary-500">
              Written by {status.provider}
              {status.model && ` (${status.model})`}. Read it before you use it. This
              goes on the record in your name.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
